import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { Column, GridApi } from 'ag-grid-enterprise';
import { uniqBy } from 'lodash';
import { inventoryDatasourceOperations } from '../datasourceOperations';
import {
  HelperTextRow,
  InventoryContext,
  WorkspaceModelAndTypeIds,
} from '../types';
import { createComponentsOperations } from './createComponentsOperations';
import { parseClipboardTextIntoComponents } from './parseClipboardTextIntoComponents';

export const getEditableWorkspaceIds = (gridApi: GridApi): ArdoqId[] => {
  const context = gridApi.getGridOption('context') as InventoryContext;
  const { datasourceSelection, accessControl } = context;

  return inventoryDatasourceOperations
    .getWorkspaceIdsFromDatasource(datasourceSelection)
    .filter(workspaceId => accessControl.canEditWorkspace(workspaceId));
};

export const getWorkspaceIdModelIdAndComponentTypeIdFromDataSource = (
  gridApi: GridApi
): WorkspaceModelAndTypeIds | null => {
  const context = gridApi.getGridOption('context') as InventoryContext;
  const { scopeData, datasourceSelection } = context;

  const [defaultWorkspaceId] = getEditableWorkspaceIds(gridApi);
  if (!defaultWorkspaceId) {
    logError(new Error(`missing defaultWorkspaceId to create component`));
    return null;
  }

  const [defaultComponentTypeId] = enhancedScopeDataOperations
    .getComponentTypeIdsByWorkspaceId(scopeData, defaultWorkspaceId)
    .filter(typeId => {
      if (datasourceSelection.dataSourceType !== 'componentType') {
        return true;
      }
      return datasourceSelection.componentTypeIds.includes(typeId);
    });

  const modelId = enhancedScopeDataOperations.getModelIdByWorkspaceId(
    scopeData,
    defaultWorkspaceId
  );

  if (!modelId || !defaultComponentTypeId) {
    logError(
      new Error(
        `missing modelId or defaultComponentTypeId in scopeData for workspace ${defaultWorkspaceId}`
      )
    );
    return null;
  }

  return {
    componentTypeId: defaultComponentTypeId,
    modelId,
    workspaceId: defaultWorkspaceId,
  };
};

const getPreviousPinnedRowsAndHelperTextRow = (
  previousPinnedRows: (APIComponentAttributes | HelperTextRow)[] | undefined
): (APIComponentAttributes | HelperTextRow)[] => {
  if (!previousPinnedRows) {
    return [createComponentsOperations.getFakeRowWithHelperText()];
  }

  return uniqBy(
    [
      ...previousPinnedRows,
      createComponentsOperations.getFakeRowWithHelperText(),
    ],
    '_id'
  );
};

const insertPinnedTopRowAndStartEditing = (
  gridApi: GridApi,
  newRow: { _id: string } & Partial<APIComponentAttributes>,
  editColKey: 'name' | 'rootWorkspace'
) => {
  const previousPinnedRows = gridApi.getGridOption('pinnedTopRowData');
  gridApi.setGridOption('pinnedTopRowData', [
    newRow,
    ...getPreviousPinnedRowsAndHelperTextRow(previousPinnedRows),
  ]);

  setTimeout(() => {
    // Delay to ensure the grid updates before editing
    gridApi.clearCellSelection();
    gridApi.startEditingCell({
      rowIndex: 0,
      colKey: editColKey,
      rowPinned: 'top',
    });
    setTimeout(() => {
      // Delay to ensure the editor cell is rendered before focusing
      const inputElement = document.querySelector<HTMLInputElement>(
        '.ag-pinned-top .ag-cell input'
      );
      if (inputElement) {
        inputElement.focus();
      }
    }, 10);
  }, 10);
};

export const insertNewComponentRow = (
  gridApi: GridApi,
  { workspaceId, componentTypeId, modelId }: WorkspaceModelAndTypeIds
) => {
  insertPinnedTopRowAndStartEditing(
    gridApi,
    {
      rootWorkspace: workspaceId,
      _id: createComponentsOperations.getComponentTemporaryId(), // AG grid requires unique IDs. They will be replaced by the backend on save
      typeId: componentTypeId,
      model: modelId,
    },
    'name'
  );
};

export const insertNewComponentRowWithEmptyWorkspace = (gridApi: GridApi) => {
  insertPinnedTopRowAndStartEditing(
    gridApi,
    { _id: createComponentsOperations.getComponentTemporaryId() },
    'rootWorkspace'
  );
};

export const openCreateComponentsDrawerWithParsedComponentValues = (
  gridApi: GridApi,
  workspaceIdModelIdAndComponentTypeId: WorkspaceModelAndTypeIds,
  clipboardText: string
) => {
  const columns = getTargetColumns(gridApi);
  const components = parseClipboardTextIntoComponents(
    clipboardText,
    workspaceIdModelIdAndComponentTypeId,
    columns
  );

  const { commands } = gridApi.getGridOption('context') as InventoryContext;
  commands.showCreateComponentsDrawer(components);
};

const getTargetColumns = (gridApi: GridApi): Column[] => {
  const columns = gridApi.getAllDisplayedColumns();
  return columns.filter(column => column.getColDef().field);
};
